import { useController, useForm } from 'react-hook-form'
import * as Components from '../components'
import * as Form from '../form'
import * as Hooks from '../hooks'
import * as Navigation from '../navigation'
import * as Stores from '../stores'
import { Image, Paragraph, View } from 'tamagui'
import { zodResolver } from '@hookform/resolvers/zod'
import { router } from 'expo-router'
import { openInAppBrowser } from '@/src/common/utils/openInAppBrowser'
import { useAlert } from '@/src/components/ui/elements/dialogs/useAlert'
import { useLoggedInQuery } from '@/src/common/gql/MesaGql'
import { GET_USER_INFO } from '@/src/common/gql/queries/user'
import { useMemo } from 'react'
import * as Mortgage from '@/src/experiences/mortgage'

const AgreementFormSchema = Form.CardApplicationFormSchema.pick({
  acceptCardholderAgreement: true,
})

export function AgreementScreen() {
  const Alert = useAlert(AgreementScreen.name)
  const updateField = Stores.useCardApplicationFormStore((s) => s.updateField)
  const terms = Hooks.useTerms()
  const { submitCardApplication } = Hooks.useSubmitCardApplication()
  const form = useForm({
    resolver: zodResolver(AgreementFormSchema),
  })
  const consentController = useController({
    control: form.control,
    name: 'acceptCardholderAgreement',
  })
  const onSubmit = form.handleSubmit(async (data) => {
    updateField('acceptCardholderAgreement', data.acceptCardholderAgreement)
    const result = await submitCardApplication()

    if (result) {
      router.push(Navigation.Routes.ApplicationProcessing.route)
    } else {
      Alert.alert(
        'Sorry!',
        "We aren't able to process your application at this time. Please try again later.",
      )
    }
  })

  const { data: userData } = useLoggedInQuery(GET_USER_INFO, {
    fetchPolicy: 'cache-first',
  })
  const sourcePartnerName = userData?.getUserInfo.sourcePartnerName ?? 'default'
  const parsedResult = Mortgage.Statsig.Store.MortgageModuleVariantName.safeParse(sourcePartnerName)
  const parsedSourcePartnerName = parsedResult.success ? parsedResult.data : 'default'

  const cardholderAgreement = useMemo(() => {
    switch (parsedSourcePartnerName) {
      case 'carrington':
        return terms.carringtonCardholderAgreement
      case 'lakeview':
        return terms.lakeviewCardholderAgreement
      case 'prmg':
      case 'encompass_lending_group':
      case 'alcova':
      case 'default':
        return terms.cardholderAgreement
      default:
        parsedSourcePartnerName satisfies never
        return terms.cardholderAgreement
    }
  }, [parsedSourcePartnerName])

  return (
    <View flex={1} bg="$white1" pb="$8">
      <Components.ApplicationScreen>
        <Components.ApplicationScreen.Header
          previousRoute={Navigation.Routes.ApplicationConfirmation}
        />
        <Components.ApplicationScreen.Content title="Last step, please review our cardholder agreement">
          <Components.FormHelpText>
            Tap document and scroll through the entire document to review.
          </Components.FormHelpText>
          <View
            onPress={() => {
              void openInAppBrowser(terms.cardholderAgreementPdf, ' ')
            }}
            group="cardHolderAgreement"
            animation="quick"
            pressStyle={{ opacity: 0.3 }}
          >
            <Image
              width="100%"
              height={300}
              borderWidth="$2"
              borderColor="$borderColor"
              borderRadius="$4"
              mb="$4"
              animation="quick"
              $group-cardHolderAgreement-hover={{ opacity: 0.1 }}
              source={{
                uri: terms.cardholderAgreementImage,
              }}
            />
            <View
              position="absolute"
              b="$0"
              l="$0"
              r="$0"
              height="$4"
              bg="rgba(0, 0, 0, 0.6)"
              justify="center"
              items="center"
              cursor="pointer"
              opacity={0}
              animation="quick"
              $group-cardHolderAgreement-hover={{ opacity: 1 }}
            >
              <Paragraph color="$white1">View Agreement →</Paragraph>
            </View>
          </View>
          <Components.FormToggle
            label="In order to proceed with your application you must confirm that you have read and consent to the following disclosures by toggling the associated switch."
            value={consentController.field.value}
            onChange={consentController.field.onChange}
          />
          <Components.FormHelpText>
            By tapping "Apply", I acknowledge that I am providing written instructions under the
            Fair Credit Reporting Act authorizing Mesa and its affiliated partners to obtain my
            credit report from one or more of the credit reporting agencies. By submitting your
            application, your full credit report will be requested from one or more of the consumer
            reporting agencies, which may negatively affect your credit score. I acknowledge that I
            have read and agree to all of the statements below:
          </Components.FormHelpText>
          <Components.FormHelpBullet>
            I have received, read, and understand the{' '}
            <Components.FormHelpLink url={cardholderAgreement.link}>
              {cardholderAgreement.title}
            </Components.FormHelpLink>
            and agree that if I am approved for and accept the Mesa Visa Signature Card, I will be
            bound to the terms and conditions set forth in the{' '}
            <Components.FormHelpLink url={cardholderAgreement.link}>
              {cardholderAgreement.title}
            </Components.FormHelpLink>
          </Components.FormHelpBullet>
          <Components.FormHelpBullet>
            I have read, understand, and agree to the{' '}
            <Components.FormHelpLink url={terms.mesaPrivacyPolicy.link}>
              {terms.mesaPrivacyPolicy.title}
            </Components.FormHelpLink>
            ,{' '}
            <Components.FormHelpLink url={terms.bankPrivacyNotice.link}>
              {terms.bankPrivacyNotice.title}
            </Components.FormHelpLink>
            ,{' '}
            <Components.FormHelpLink url={terms.mesaPrivacyNotice.link}>
              {terms.mesaPrivacyNotice.title}
            </Components.FormHelpLink>
            ,{' '}
            <Components.FormHelpLink url={terms.termsOfService.link}>
              {terms.termsOfService.title}
            </Components.FormHelpLink>
            ,{' '}
            <Components.FormHelpLink url={terms.eSignConsentPolicy.link}>
              {terms.eSignConsentPolicy.title}
            </Components.FormHelpLink>
            ,{' '}
            <Components.FormHelpLink url={terms.creditReportAuthorizationNotice.link}>
              {terms.creditReportAuthorizationNotice.title}
            </Components.FormHelpLink>
            <>
              and{' '}
              <Components.FormHelpLink url={cardholderAgreement.link}>
                {cardholderAgreement.title}
              </Components.FormHelpLink>
            </>
          </Components.FormHelpBullet>
          <Components.FormHelpBullet>
            I have received, read, and understand the{' '}
            <Components.FormHelpLink url={terms.usaPatriotActNotice.link}>
              {terms.usaPatriotActNotice.title}
            </Components.FormHelpLink>
            .
          </Components.FormHelpBullet>
        </Components.ApplicationScreen.Content>
        <Components.ApplicationScreen.SubmitButton
          onPress={() => void onSubmit()}
          disabled={!form.formState.isValid}
          loading={form.formState.isSubmitting}
          title="Apply"
        />
      </Components.ApplicationScreen>
    </View>
  )
}
